import { useEffect, useState } from "react";
import moment from "moment";
import { TypedFilter } from "odata-query";
import { ALL, HTTP_STATUSES, NONE } from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import {
  IBidResponse,
  IPlanCountResponse,
  PlanCountType,
  PlanStatus,
} from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import { useBids, useDepartments } from "@mibuildweb/hooks";
import { BidTypedFilter, GetBidsParams } from "./types";

type CountFilterKey =
  | "assigneeId"
  | "builderId"
  | "communityId"
  | "seriesGroupId"
  | "seriesId"
  | "planStatus";

const guidFilterKeys = new Set<string>([
  "assigneeId",
  "builderId",
  "communityId",
  "seriesGroupId",
  "seriesId",
]);
const countFilterKeys = new Set<string>([...guidFilterKeys, "planStatus"]);

export const isCountFilterKey = (key: string): key is CountFilterKey =>
  countFilterKeys.has(key);

export const tabToPlanStatusMap: { [key: string]: PlanStatus } = {
  new: "Assignment",
  setup: "Setup",
  pricing: "Pricing",
  approved: "Approved",
};

export const tabToPlanCountTypeMap: { [key: string]: PlanCountType } = {
  new: "NotStarted",
  setup: "Assignee",
  pricing: "Customer",
  approved: "Customer",
};

export const getDaysUntilDue = (bid: IBidResponse) => {
  const currentDate = moment();
  const dueDate = moment(bid.bidRequest?.dueDate);
  return dueDate.diff(currentDate, "days");
};

export const useBidsViewModel = (initialSearch = "") => {
  const [search, setSearch] = useState<string>(initialSearch);
  const [tabCounts, setTabCounts] = useState<IPlanCountResponse[]>([]);
  const [bidFilterCounts, setBidFilterCounts] = useState<IPlanCountResponse[]>(
    []
  );

  const { navigate, params } = useRouter();

  const debouncedSearch = useDebounce(search);

  const {
    isLoading,
    bids,
    setBids,
    getBids,
    getPlanCounts,
    deleteBid,
    assignBid,
    updateBidStatus,
    updateBid,
    refresh: bidsRefreshToken,
  } = useBids();
  const { departments, getDepartments } = useDepartments();

  useEffect(() => {
    const getTabCounts = async () => {
      const response = await getPlanCounts({ planCountType: "Status" });

      if (response.status === HTTP_STATUSES.OK) {
        setTabCounts(response.data);
      }
    };

    getTabCounts();
  }, [params.tab]);

  useEffect(() => {
    const tab = params.tab as string | undefined;
    const planStatus = tab ? tabToPlanStatusMap[tab] : undefined;
    const planCountType = tab ? tabToPlanCountTypeMap[tab] : undefined;

    if (!planStatus || !planCountType) {
      setBidFilterCounts([]);
      return;
    }

    const getFilterCounts = async () => {
      const response = await getPlanCounts({ planCountType, planStatus });

      if (response.status === HTTP_STATUSES.OK) {
        setBidFilterCounts(response.data);
      }
    };

    getFilterCounts();
  }, [params.tab]);

  const getBidsFiltered = (params: GetBidsParams = {}) => {
    const { filters, countFilters, skip, top } = params;
    const searchQuery = buildSearchQuery(search, ["name"]);
    const filterObj: BidTypedFilter = {
      ...searchQuery,
      not: { builderId: { eq: { type: "guid", value: null } } },
    };

    let sortBy = "name asc";

    if (filters) {
      if (filters.builderId && filters.builderId !== ALL) {
        filterObj.builderId = {
          eq: { type: "guid", value: filters.builderId },
        };
      }
      if (filters.communityId && filters.communityId !== ALL) {
        filterObj.communityId = {
          eq: { type: "guid", value: filters.communityId },
        };
      }
      if (filters.seriesGroupId && filters.seriesGroupId !== ALL) {
        filterObj.seriesGroupId = {
          eq: { type: "guid", value: filters.seriesGroupId },
        };
      }
      if (filters.seriesId && filters.seriesId !== ALL) {
        filterObj.seriesId = {
          eq: { type: "guid", value: filters.seriesId },
        };
      }
      if (filters.assigneeId && filters.assigneeId !== ALL) {
        filterObj.assigneeId = {
          eq: { type: "guid", value: filters.assigneeId },
        };
      }
      if (filters.planStatus && filters.planStatus !== ALL) {
        filterObj.planStatus = { eq: filters.planStatus };
      }
      if (filters.sortBy && filters.sortBy !== NONE) {
        sortBy = filters.sortBy;
      }
    }

    const negatedCountFilters: BidTypedFilter[] = (countFilters ?? [])
      .filter(({ key, value }) => isCountFilterKey(key) && value === "ne null")
      .map(({ key }) => ({
        not: { [key]: { eq: null } } as TypedFilter<IBidResponse>,
      }));

    countFilters?.forEach(({ key, value }) => {
      if (!isCountFilterKey(key)) return;

      const [operator, filterValue] = value.split(" ", 2);
      if ((operator !== "eq" && operator !== "ne") || !filterValue) return;

      const condition =
        filterValue === "null"
          ? { eq: null }
          : {
              eq: guidFilterKeys.has(key)
                ? { type: "guid", value: filterValue }
                : filterValue,
            };

      if (operator === "ne" && filterValue === "null") return;

      filterObj[key] = condition;
    });
    if (negatedCountFilters.length) {
      filterObj.and = negatedCountFilters;
    }

    return getBids({
      skip,
      top,
      filters: filterObj,
      sort: sortBy,
    });
  };

  const handleAssignBid = async (
    bid: IBidResponse,
    assigneeId: string | null
  ) => {
    const response = await assignBid(bid, assigneeId);

    if (response.status === HTTP_STATUSES.OK) {
      const tab = params.tab as string | undefined;
      const planStatus = tab ? tabToPlanStatusMap[tab] : undefined;
      const planCountType = tab ? tabToPlanCountTypeMap[tab] : undefined;

      if (planStatus && planCountType) {
        const countsResponse = await getPlanCounts({
          planCountType,
          planStatus,
        });

        if (countsResponse.status === HTTP_STATUSES.OK) {
          setBidFilterCounts(countsResponse.data);
        }
      }
    }
  };

  const handleStartTask = async (bid: IBidResponse) => {
    const response = await updateBidStatus(bid, "Setup");

    if (response.status === HTTP_STATUSES.OK) {
      setBids((prev) =>
        prev.map((b) => (b.id === bid.id ? { ...response.data } : b))
      );
      navigate(`/estimating/bids/create/${bid.id}`);
    }
  };

  return {
    isLoading,
    bids,
    setBids,
    tabCounts,
    bidFilterCounts,
    getBidsFiltered,
    updateBid,
    search,
    setSearch,
    debouncedSearch,
    departments,
    getDepartments,
    deleteBid,
    getDaysUntilDue,
    handleAssignBid,
    handleStartTask,
    bidsRefreshToken,
  };
};
